import React, {useCallback, useRef} from 'react';
import {
  Keyboard,
  Platform,
  TextInput,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import {ScrollView} from 'react-native-gesture-handler';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';

import {ExitPopup} from '/component/base/ExitPopup';
import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Text from '/component/base/Text';
import ModalHeader from '/component/layout/ModalHeader';
import {SocialLinksTestIDs} from '/constant/TestIDs';
import {usePopup} from '/provider/PopupProvider';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {StaticFocusable} from '../sections/StaticFocusable';
import {
  StickyActionFooter,
  useKeyboardVisible,
} from '../StickyActionFooter/StickyActionFooter';
import {ErrorBanner} from '../widgets/ErrorBanner/ErrorBanner';
import {FieldWrapper} from '../widgets/FieldWrapper';

import {useSocialLinks} from './SocialLinksScreen.hook';
import {styles} from './SocialLinksScreen.style';
import {Props, SocialFieldProps} from './SocialLinksScreen.types';

const SocialField = ({
  link,
  value,
  errorText,
  isSaving,
  onChange,
  onBlur,
}: SocialFieldProps) => {
  const inputRef = useRef<TextInput>(null);

  const isBlog = link?.type?.toLowerCase().includes('blog');
  const hasError = !!errorText;
  const fieldType = isBlog ? 'URL' : 'username';

  const baseInstruction = isBlog
    ? 'Enter your blog URL.'
    : `Enter username after ${link.baseUrl}.`;

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const accessibilityLabel = `${link.type}, ${fieldType}${
    value ? `, ${value}` : ', empty'
  }${hasError ? `, error: ${errorText}` : ''}${
    baseInstruction ? `. ${baseInstruction}` : ''
  }`;
  const accessibilityHint = value
    ? 'Double tap to edit'
    : 'Double tap to enter value';

  const labelID = `label-${link.socialNetworkID}`;

  const inputNode = (
    <View
      style={[
        styles.inputRow,
        hasError ? styles.inputRowError : null,
        isSaving ? styles.inputRowDisabled : null,
      ]}
      testID={SocialLinksTestIDs.field.inputRow(link.type)}
      pointerEvents="box-none"
      accessible={false}>
      <View
        style={styles.baseUrlContainer}
        pointerEvents="none"
        accessible={false}
        testID={SocialLinksTestIDs.field.baseUrl(link.type)}>
        <Text
          numberOfLines={1}
          allowFontScaling={true}
          maxFontSizeMultiplier={2}
          style={styles.baseUrlText}
          accessible={false}
          testID={SocialLinksTestIDs.field.baseUrlText(link.type)}>
          {link.baseUrl}
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <TextInput
          ref={inputRef}
          accessible
          importantForAccessibility="yes"
          style={styles.textInput}
          value={value}
          onChangeText={nextValue => onChange(link.socialNetworkID, nextValue)}
          onBlur={() => onBlur?.(link?.socialNetworkID)}
          placeholder={isBlog ? 'www.yourblog.com' : link?.type?.toLowerCase()}
          placeholderTextColor={styles.customPlaceholder?.color || '#999'}
          editable={!isSaving}
          autoCapitalize="none"
          autoCorrect={false}
          returnKeyType="next"
          submitBehavior="submit"
          onSubmitEditing={() => Keyboard.dismiss()}
          accessibilityLabel={accessibilityLabel}
          accessibilityLabelledBy={labelID}
          testID={SocialLinksTestIDs.field.input(link.type)}
          allowFontScaling
          maxFontSizeMultiplier={2}
        />
      </View>
    </View>
  );

  return (
    <FieldWrapper
      key={link.socialNetworkID}
      label={link.type}
      labelID={labelID}
      errorText={errorText}
      testID={SocialLinksTestIDs.field.row(link.type)}
      labelTestID={SocialLinksTestIDs.field.label(link.type)}
      errorTestID={SocialLinksTestIDs.field.error(link.type)}>
      {Platform.OS === 'android' ? (
        <KeyboardFocusable
          testID={`${SocialLinksTestIDs.field.input(link.type)}-focusable`}
          accessible
          accessibilityRole="button"
          accessibilityLabel={`${link.type}, ${fieldType}, ${value || 'empty'}${
            hasError ? `, error: ${errorText}` : ''
          }`}
          accessibilityHint={accessibilityHint}
          style={styles.textInputFocusable}
          IsBorder={false}
          onPress={focusInput}>
          <View accessible={false}>{inputNode}</View>
        </KeyboardFocusable>
      ) : (
        inputNode
      )}
    </FieldWrapper>
  );
};

export const SocialLinksScreen = ({
  onClose,
  onSave,
  isSaving = false,
  socialLinks = [],
}: Props) => {
  // @ts-expect-error existing popup typing mismatch
  const {showPopup, hidePopup} = usePopup();

  const scrollRef = useRef<KeyboardAwareScrollView>(null);

  const {values, derived, actions} = useSocialLinks({
    socialLinks,
    onSave,
  });

  const isKeyboardVisible = useKeyboardVisible();
  const shouldShowFooter = !isKeyboardVisible && (derived.isDirty || isSaving);

  // Extra bottom padding when keyboard is not visible and footer is not shown
  const outerBottomPadding = shouldShowFooter
    ? 0
    : Platform.OS === 'android'
    ? 96
    : 48;

  const scrollBottomPadding = shouldShowFooter
    ? Platform.OS === 'android'
      ? 104
      : 88
    : 0;

  const scrollToTop = () => {
    const node =
      scrollRef.current?.getScrollResponder() as unknown as ScrollView | null;
    if (node) {
      node.scrollTo({x: 0, y: 0, animated: true});
    } else {
      scrollRef.current?.scrollToPosition?.(0, 0, true);
    }
  };

  const handleSaveWithScroll = () => {
    actions.handleSave();
    Keyboard.dismiss();

    setTimeout(
      () => {
        scrollToTop();
      },
      Platform.OS === 'android' ? 300 : 100,
    );
  };

  const errorCount = Object.values(values.errors).filter(Boolean).length;

  const socialLinksInstructions = tt(
    'socialLinksInstruction',
    'For Social Media, please enter only the URL text that appears after the ".com/". (example: enter "janedoe" for your https://facebook.com/janedoe page)',
  );

  const handleBackPress = () => {
    if (isSaving) {
      return;
    }

    if (!derived.isDirty) {
      onClose();
      return;
    }

    showPopup(
      ExitPopup,
      {
        title: 'Are you sure you want to go back?',
        description: 'All unsaved changes will be lost.',
        alignButtonsAsColumn: true,
        handleCancelButton: hidePopup,
        handleDiscardButton: async () => {
          await hidePopup();
          actions.resetState();
          onClose();
        },
        exitButton: hidePopup,
        discardButtonStyle: {backgroundColor: '#000'},
      },
      {avoidable: false, showFullModal: false},
    );
  };

  const handleCancelChanges = () => {
    Keyboard.dismiss();
    actions.resetState();
  };

  return (
    <ModalHeader
      testID={SocialLinksTestIDs.header}
      title={tt('socialMedia', 'Social Media')}
      accessibilityLabel="Social Media"
      closeAccessibilityLabel="Back to previous screen"
      onClose={handleBackPress}
      closeIconName="chevron-left"
      style={{
        base: styles.base,
        headerText: styles.headerText,
        actionIcon: styles.goBackIcon,
      }}
      allowTitleFontScaling
      titleMaxFontSizeMultiplier={2}
      allowActionFontScaling
      actionMaxFontSizeMultiplier={2}
      useAccessibleTitleContainer>
      {Platform.OS === 'web' ? (
        <View
          style={[styles.scrollViewBase, {paddingBottom: outerBottomPadding}]}
          pointerEvents="box-none">
          <KeyboardAwareScrollView
            ref={scrollRef}
            enableResetScrollToCoords={false}
            contentContainerStyle={[
              styles.content,
              {paddingBottom: scrollBottomPadding},
            ]}
            style={styles.scrollViewBase}
            showsVerticalScrollIndicator={false}
            keyboardDismissMode="on-drag"
            enableOnAndroid
            enableAutomaticScroll
            extraScrollHeight={Platform.OS === 'android' ? 150 : 80}
            extraHeight={Platform.OS === 'android' ? 150 : 80}
            keyboardShouldPersistTaps="handled"
            accessible={false}
            nestedScrollEnabled
            scrollEventThrottle={16}>
            <View style={styles.instructionContainer} accessible={false}>
              <StaticFocusable
                testID={SocialLinksTestIDs.instruction.heading}
                label="Social Media section"
                role="header">
                <Text
                  style={styles.instructionHeader}
                  accessible={false}
                  allowFontScaling
                  maxFontSizeMultiplier={2}
                  testID={SocialLinksTestIDs.instruction.headingText}>
                  {tt('socialMedia', 'Social Media')}
                </Text>
              </StaticFocusable>

              <StaticFocusable
                testID={SocialLinksTestIDs.instruction.instructions}
                label={String(socialLinksInstructions)}>
                <Text
                  accessible={false}
                  style={styles.instructionText}
                  allowFontScaling
                  maxFontSizeMultiplier={2}
                  testID={SocialLinksTestIDs.instruction.instructionsText}>
                  {socialLinksInstructions}
                </Text>
              </StaticFocusable>

              <ErrorBanner
                testID={SocialLinksTestIDs.errorBanner.container}
                errorCount={errorCount}
                hasSubmitted={derived.hasSubmitted}
              />
            </View>
            {values.links.map(link => (
              <SocialField
                key={link.socialNetworkID}
                link={link}
                value={link.url}
                errorText={
                  values.touchedFields?.[link.socialNetworkID] ||
                  derived.hasSubmitted
                    ? values.errors[link.socialNetworkID]
                    : undefined
                }
                isSaving={isSaving}
                onChange={actions.handleChange}
                onBlur={actions.handleBlur}
              />
            ))}
          </KeyboardAwareScrollView>

          {shouldShowFooter && (
            <StickyActionFooter
              onCancel={isSaving ? () => {} : handleCancelChanges}
              onSave={handleSaveWithScroll}
              isSaving={isSaving}
              showCancel
              showSave
              disableSave={isSaving || !derived.isDirty}
              saveTestID={SocialLinksTestIDs.footer.save}
            />
          )}

          {isSaving && (
            <View style={styles.containerOverlay} pointerEvents="box-only" />
          )}
        </View>
      ) : (
        <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
          <View
            style={[styles.scrollViewBase, {paddingBottom: outerBottomPadding}]}
            pointerEvents="box-none">
            <KeyboardAwareScrollView
              ref={scrollRef}
              enableResetScrollToCoords={false}
              contentContainerStyle={[
                styles.content,
                {paddingBottom: scrollBottomPadding},
              ]}
              style={styles.scrollViewBase}
              showsVerticalScrollIndicator={false}
              keyboardDismissMode="on-drag"
              enableOnAndroid
              enableAutomaticScroll
              extraScrollHeight={Platform.OS === 'android' ? 150 : 80}
              extraHeight={Platform.OS === 'android' ? 150 : 80}
              keyboardShouldPersistTaps="handled"
              accessible={false}
              nestedScrollEnabled
              scrollEventThrottle={16}>
              <View style={styles.instructionContainer} accessible={false}>
                <StaticFocusable
                  testID={SocialLinksTestIDs.instruction.heading}
                  label="Social Media section"
                  role="header">
                  <Text
                    style={styles.instructionHeader}
                    accessible={false}
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    testID={SocialLinksTestIDs.instruction.headingText}>
                    {tt('socialMedia', 'Social Media')}
                  </Text>
                </StaticFocusable>

                <StaticFocusable
                  testID={SocialLinksTestIDs.instruction.instructions}
                  label={String(socialLinksInstructions)}>
                  <Text
                    accessible={false}
                    style={styles.instructionText}
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    testID={SocialLinksTestIDs.instruction.instructionsText}>
                    {socialLinksInstructions}
                  </Text>
                </StaticFocusable>

                <ErrorBanner
                  testID={SocialLinksTestIDs.errorBanner.container}
                  errorCount={errorCount}
                  hasSubmitted={derived.hasSubmitted}
                />
              </View>
              {values.links.map(link => (
                <SocialField
                  key={link.socialNetworkID}
                  link={link}
                  value={link.url}
                  errorText={
                    values.touchedFields?.[link.socialNetworkID] ||
                    derived.hasSubmitted
                      ? values.errors[link.socialNetworkID]
                      : undefined
                  }
                  isSaving={isSaving}
                  onChange={actions.handleChange}
                  onBlur={actions.handleBlur}
                />
              ))}
            </KeyboardAwareScrollView>

            {shouldShowFooter && (
              <StickyActionFooter
                onCancel={isSaving ? () => {} : handleCancelChanges}
                onSave={handleSaveWithScroll}
                isSaving={isSaving}
                showCancel
                showSave
                disableSave={isSaving || !derived.isDirty}
                saveTestID={SocialLinksTestIDs.footer.save}
              />
            )}

            {isSaving && (
              <View style={styles.containerOverlay} pointerEvents="box-only" />
            )}
          </View>
        </TouchableWithoutFeedback>
      )}
    </ModalHeader>
  );
};
